import { useState, useEffect, useMemo } from 'react';
import { calculateAnsweredCount, calculateProgressPercent } from '../../utils/liveSession/answerUtils';
import { QuestionDisplay } from './QuestionDisplay';
import { QuestionNavigation } from './QuestionNavigation';
import { EnergyBaselineForm } from './EnergyBaselineForm';

/**
 * Helper function to check if energy baseline is complete
 */
const isEnergyBaselineComplete = (session) => {
  if (!session?.notes) return false;
  
  try {
    const notesData = typeof session.notes === 'string' ? JSON.parse(session.notes) : session.notes;
    const energyBaseline = notesData.energyBaseline;
    
    if (!energyBaseline) return false;
    
    // Check required fields from Energy Baseline Information
    const hasAnnualKwh = energyBaseline.annualKwh && energyBaseline.annualKwh.toString().trim();
    const hasAnnualElectricityCost = energyBaseline.annualElectricityCost && energyBaseline.annualElectricityCost.toString().trim();
    const hasAverageMonthlyBill = energyBaseline.averageMonthlyBill && energyBaseline.averageMonthlyBill.toString().trim();
    
    // Check required fields from Production Information
    const hasAnnualProduction = energyBaseline.annualProduction && energyBaseline.annualProduction.toString().trim();
    const hasProductionUnit = energyBaseline.productionUnit && energyBaseline.productionUnit.trim();
    
    return hasAnnualKwh && hasAnnualElectricityCost && hasAverageMonthlyBill && hasAnnualProduction && hasProductionUnit;
  } catch (e) {
    return false;
  }
};

/**
 * Main assessment questions area component
 */
export const AssessmentQuestionsArea = ({
  session,
  currentQuestionIndex,
  currentAnswers,
  savingAnswers,
  isConsultant,
  completingSession,
  onAnswerChange,
  onNextQuestion,
  onPreviousQuestion,
  onSubmit,
  fetchSession
}) => {
  const [energyBaselineComplete, setEnergyBaselineComplete] = useState(false);
  
  // Check energy baseline completion status
  useEffect(() => {
    if (session) {
      const isComplete = isEnergyBaselineComplete(session);
      setEnergyBaselineComplete(isComplete);
    }
  }, [session]);
  
  const handleEnergyBaselineComplete = async () => {
    // Refresh session to get updated notes
    if (fetchSession) {
      await fetchSession();
    }
    setEnergyBaselineComplete(true);
  };
  
  // Recalculate progress when answers change (reactive)
  const answeredCount = useMemo(() => {
    return calculateAnsweredCount(currentAnswers);
  }, [currentAnswers]);
  
  const progressPercent = useMemo(() => {
    return calculateProgressPercent(answeredCount, session?.questions_count || 0);
  }, [answeredCount, session?.questions_count]);
  
  // Show energy baseline form if not complete and session is active (only for ESA)
  const shouldShowEnergyBaseline = session?.assessment_type === 'ESA' && 
                                    session?.status === 'active' && 
                                    !energyBaselineComplete;

  return (
    <div className="lg:col-span-2">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        
        {/* Assessment Header */}
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Assessment Questions</h2>
              {session?.industry && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {session.industry.name}
                  {session.sub_industry && ` > ${session.sub_industry.name}`}
                </p>
              )}
            </div>
            {session?.questions_count > 0 && (
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {answeredCount}/{session.questions_count}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Questions Answered</div>
              </div>
            )}
          </div>
          
          {/* Progress Bar */}
          {session?.questions_count > 0 && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div 
                  className="bg-gradient-to-r from-emerald-500 to-blue-500 dark:from-emerald-400 dark:to-blue-400 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 text-center">
                {progressPercent}% Complete
                {answeredCount === session.questions_count && ' 🎉'}
              </p>
            </div>
          )}
        </div>

        {/* Questions Display */}
        <div className="p-6">
          {/* Consultant View-Only Notice */}
          {isConsultant && session?.status === 'active' && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-300 dark:border-blue-700 rounded-lg mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <div>
                  <p className="text-blue-900 dark:text-blue-200 font-semibold mb-1">👀 Viewing Live Session (Read-Only)</p>
                  <p className="text-blue-800 dark:text-blue-300 text-sm">
                    You're observing the user's assessment in real-time. The user controls navigation and answer selection. Changes will appear instantly without refresh.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Status-specific messages */}
          {session?.status === 'completed' && (
            <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg mb-6">
              <p className="text-green-800 dark:text-green-300 font-medium">
                ✅ This session has been completed. Redirecting...
              </p>
            </div>
          )}
          
          {session?.status === 'cancelled' && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg mb-6">
              <p className="text-red-800 dark:text-red-300 font-medium">
                ❌ This session has been cancelled. Redirecting...
              </p>
            </div>
          )}
          
          {session?.status === 'pending' && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg mb-6">
              <p className="text-yellow-800 dark:text-yellow-300 font-medium">
                ⏳ Session is pending. Waiting for both parties to join...
              </p>
            </div>
          )}

          {/* Energy Baseline Form - Show before questions if not complete (ESA only) */}
          {shouldShowEnergyBaseline ? (
            <EnergyBaselineForm
              session={session}
              isConsultant={isConsultant}
              onComplete={handleEnergyBaselineComplete}
            />
          ) : (
            <>
              {/* Questions Display - One at a time with navigation */}
              {session?.questions && session.questions.length > 0 ? (
            <div className="space-y-6">
              {/* Current Question Progress Indicator */}
              <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Question {currentQuestionIndex + 1} of {session.questions.length}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {Math.round(((currentQuestionIndex + 1) / session.questions.length) * 100)}% Progress
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-emerald-500 to-blue-500 dark:from-emerald-400 dark:to-blue-400 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentQuestionIndex + 1) / session.questions.length) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Single Question Display */}
              <QuestionDisplay
                question={session.questions[currentQuestionIndex]}
                index={currentQuestionIndex}
                currentAnswers={currentAnswers}
                savingAnswers={savingAnswers}
                isConsultant={isConsultant}
                sessionStatus={session?.status || 'pending'}
                onAnswerChange={onAnswerChange}
              />
              
              {/* Navigation Buttons */}
              <QuestionNavigation
                currentQuestionIndex={currentQuestionIndex}
                totalQuestions={session.questions.length}
                onPrevious={onPreviousQuestion}
                onNext={onNextQuestion}
                onSubmit={onSubmit}
                isSubmitting={completingSession}
                isConsultant={isConsultant}
              />
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600 dark:text-gray-400 mb-2 font-medium">No Questions Available</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {!session?.industry_id 
                  ? 'This session does not have an industry assigned. Please restart the session with industry selection.'
                  : 'No questions found for the selected industry and assessment type.'
                }
              </p>
            </div>
          )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

